import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Music, LogOut, User, Radio, Search, MessageCircle, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadMessagesCount } from '../../hooks/useUnreadMessagesCount';

interface NavbarProps {
  scrolled: boolean;
}

interface NavItem {
  name: string;
  path: string;
  icon?: React.ReactNode;
}

const Navbar: React.FC<NavbarProps> = ({ scrolled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const unreadMessagesCount = useUnreadMessagesCount(user?.id);
  const unreadBadgeText = unreadMessagesCount > 99 ? '99+' : String(unreadMessagesCount);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const authLinks: NavItem[] = [
    { name: 'Search', path: '/search', icon: <Search className="w-5 h-5 mr-2" /> },
    { name: 'Messages', path: '/messages', icon: <MessageCircle className="w-5 h-5 mr-2" /> },
    { name: 'Garage', path: '/garage', icon: <Radio className="w-5 h-5 mr-2" /> },
    { name: 'Profile', path: '/profile', icon: <User className="w-5 h-5 mr-2" /> },
    { name: 'Settings', path: '/settings', icon: <Settings className="w-5 h-5 mr-2" /> },
  ];

  const guestLinks: NavItem[] = [
    { name: 'Login', path: '/login' },
    { name: 'Register', path: '/register' },
  ];

  const navLinks: NavItem[] = [
    ...(isAuthenticated ? authLinks : guestLinks),
  ];

  return (
    <header 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-gray-900 bg-opacity-95 shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          <NavLink to={isAuthenticated ? '/search' : '/'} className="flex items-center">
            <Music className="w-8 h-8 text-purple-500" />
            <span className="ml-2 text-xl font-bold">JustJam</span>
          </NavLink>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.path}
                className={({ isActive }) =>
                  `flex items-center text-sm uppercase tracking-wider font-medium transition-colors hover:text-purple-400 ${
                    isActive ? 'text-purple-500' : 'text-gray-200'
                  }`
                }
              >
                {link.icon && link.icon}
                {link.name}
                {link.path === '/messages' && unreadMessagesCount > 0 && (
                  <span className="ml-2 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-purple-500 text-white text-[10px] font-semibold flex items-center justify-center">
                    {unreadBadgeText}
                  </span>
                )}
              </NavLink>
            ))}
            
            {isAuthenticated && (
              <button 
                onClick={handleLogout}
                className="flex items-center text-sm uppercase tracking-wider font-medium transition-colors text-gray-200 hover:text-purple-400"
              >
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-gray-200 focus:outline-none"
            onClick={toggleMenu}
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div 
          className="md:hidden bg-gray-900 bg-opacity-95"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="container mx-auto px-4 py-4">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <NavLink
                  key={link.name}
                  to={link.path}
                  className={({ isActive }) =>
                    `flex items-center text-sm uppercase tracking-wider font-medium transition-colors hover:text-purple-400 p-2 ${
                      isActive ? 'text-purple-500 bg-gray-800 bg-opacity-50 rounded' : 'text-gray-200'
                    }`
                  }
                  onClick={() => setIsOpen(false)}
                >
                  {link.icon && link.icon}
                  {link.name}
                  {link.path === '/messages' && unreadMessagesCount > 0 && (
                    <span className="ml-2 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-purple-500 text-white text-[10px] font-semibold flex items-center justify-center">
                      {unreadBadgeText}
                    </span>
                  )}
                </NavLink>
              ))}
              
              {isAuthenticated && (
                <button 
                  onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }}
                  className="flex items-center text-sm uppercase tracking-wider font-medium transition-colors text-gray-200 hover:text-purple-400 p-2"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Logout
                </button>
              )}
            </nav>
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Navbar;
